import Service from 'react-reservice';

import {
  IGetOrderCountsResponse,
  IOrder,
  IOrderHistory,
  IPendingOrder,
  IUIOrderTableDisplay
} from '@aqx-core/common/api/order';
import { EColumnPreferenceOrderType } from '@aqx-core/common/api/user-account-setting';
import { IAssetsFilterValues } from '@aqx-core/common/services/assets-filter/types';

import { DefaultFilterSortValue } from './constants';
import { IOrderHistoryDateRange } from './types';
import { getDefaultHistoryDateRange } from './utils';

export interface IOrderListingContext {
  isTradePage: boolean;

  openPositionLoading?: boolean;
  pendingOrderLoading?: boolean;
  orderHistoryLoading?: boolean;
  bulkCloseLoading?: boolean;

  orderSelectedTab: EColumnPreferenceOrderType;
  orderHistorySelectedTab: EColumnPreferenceOrderType;
  marketOrderCounts: number;
  pendingOrderCounts: number;

  initialLoadOpenPositions: boolean;
  initialLoadPendingOrders: boolean;
  initialLoadOrderHistories: boolean;
  initialLoadHistoryDeals: boolean;
  initialLoadHistoryOrdersCentroid: boolean;

  mappedOpenPositions: Record<string, IOrder>;
  mappedPendingOrders: Record<string, IPendingOrder>;
  mappedOrderHistories: Record<string, IOrderHistory>;

  openPositionIds: string[];
  pendingOrderIds: string[];
  orderHistoryIds: string[];

  openPositionsTableDisplay: IOrder[];
  pendingOrdersTableDisplay: IPendingOrder[];
  orderHistoryTableDisplay: IUIOrderTableDisplay[];

  selectedSymbol?: string;
  isShowAllSymbol: boolean;
  isShowAllSymbolLoaded: boolean;
  isAllSymbolFetched?: boolean;
  orderHistoryDateRange: IOrderHistoryDateRange;

  isOrderDetailLoading: boolean;
  orderDetail: IOrder | undefined;

  searchText: string;
  // Filter and Sort
  filterFormValues?: IAssetsFilterValues;

  // Filtered + sorted results (updated whenever filter/sort changes)
  filteredOpenPositions: IOrder[];
  filteredPendingOrders: IPendingOrder[];
}

const defaultContext: IOrderListingContext = {
  isTradePage: false,

  openPositionLoading: true,
  pendingOrderLoading: true,
  orderHistoryLoading: true,
  bulkCloseLoading: false,

  orderSelectedTab: EColumnPreferenceOrderType.OPEN_POSITION,
  orderHistorySelectedTab: EColumnPreferenceOrderType.POSITION_HISTORY,
  marketOrderCounts: 0,
  pendingOrderCounts: 0,

  initialLoadOpenPositions: true,
  initialLoadPendingOrders: true,
  initialLoadOrderHistories: true,
  initialLoadHistoryDeals: true,
  initialLoadHistoryOrdersCentroid: true,

  mappedOpenPositions: {},
  mappedPendingOrders: {},
  mappedOrderHistories: {},

  openPositionIds: [],
  pendingOrderIds: [],
  orderHistoryIds: [],

  openPositionsTableDisplay: [],
  pendingOrdersTableDisplay: [],
  orderHistoryTableDisplay: [],

  selectedSymbol: undefined,
  isShowAllSymbol: false,
  isShowAllSymbolLoaded: false,
  isAllSymbolFetched: false,
  orderHistoryDateRange: getDefaultHistoryDateRange(),

  isOrderDetailLoading: false,
  orderDetail: undefined,

  searchText: '',
  filterFormValues: DefaultFilterSortValue,

  filteredOpenPositions: [],
  filteredPendingOrders: []
};

class OrderListingService extends Service<IOrderListingContext> {
  constructor() {
    super();
    this.context = { ...defaultContext };
  }

  reset = () => {
    this.context = { ...defaultContext };
  };

  setSearchText = (searchText: string) => {
    this.context.searchText = searchText;
  };

  setIsTradePage = (isTradePage: boolean) => {
    this.context.isTradePage = isTradePage;
  };

  setOpenPositionLoading = (loading: boolean) => {
    this.context.openPositionLoading = loading;
  };

  setPendingOrderLoading = (loading: boolean) => {
    this.context.pendingOrderLoading = loading;
  };

  setOrderHistoryLoading = (loading: boolean) => {
    this.context.orderHistoryLoading = loading;
  };

  setBulkCloseLoading = (loading: boolean) => {
    this.context.bulkCloseLoading = loading;
  };

  setOrderSelectedTab = (values: EColumnPreferenceOrderType) => {
    this.context.orderSelectedTab = values;
  };

  setOrderHistorySelectedTab = (values: EColumnPreferenceOrderType) => {
    this.context.orderHistorySelectedTab = values;
  };

  setOrderCounts = (values: IGetOrderCountsResponse) => {
    this.context.marketOrderCounts = values.marketOrderCounts;
    this.context.pendingOrderCounts = values.pendingOrderCounts;
  };

  setInitialLoadOpenPositions = (value: boolean) => {
    this.context.initialLoadOpenPositions = value;
  };

  setInitialLoadPendingOrders = (value: boolean) => {
    this.context.initialLoadPendingOrders = value;
  };

  setInitialLoadOrderHistories = (value: boolean) => {
    this.context.initialLoadOrderHistories = value;
  };

  setInitialLoadHistoryDeals = (value: boolean) => {
    this.context.initialLoadHistoryDeals = value;
  };

  setInitialLoadHistoryOrdersCentroid = (value: boolean) => {
    this.context.initialLoadHistoryOrdersCentroid = value;
  };

  setOpenPositionIds = (values: string[]) => {
    this.context.openPositionIds = [...values];
  };

  setPendingOrderIds = (values: string[]) => {
    this.context.pendingOrderIds = [...values];
  };

  setOrderHistoryIds = (values: string[]) => {
    this.context.orderHistoryIds = [...values];
  };

  setMappedOpenPositions = (values: Record<string, IOrder>) => {
    this.context.mappedOpenPositions = { ...values };
  };

  setMappedPendingOrders = (values: Record<string, IPendingOrder>) => {
    this.context.mappedPendingOrders = { ...values };
  };

  setMappedOrderHistories = (values: Record<string, IOrderHistory>) => {
    this.context.mappedOrderHistories = { ...values };
  };

  setOpenPositionsTableDisplay = (values: IOrder[]) => {
    this.context.openPositionsTableDisplay = [...values];
  };

  setPendingOrdersTableDisplay = (values: IPendingOrder[]) => {
    this.context.pendingOrdersTableDisplay = [...values];
  };

  setOrderHistoryTableDisplay = (values: IUIOrderTableDisplay[]) => {
    this.context.orderHistoryTableDisplay = [...values];
  };

  setSelectedSymbol = (values?: string) => {
    this.context.selectedSymbol = values;
  };

  setIsShowAllSymbol = (values: boolean) => {
    this.context.isShowAllSymbol = values;
  };

  setIsShowAllSymbolLoaded = (value: boolean) => {
    this.context.isShowAllSymbolLoaded = value;
  };

  setIsAllSymbolFetched = (values: boolean) => {
    this.context.isAllSymbolFetched = values;
  };

  setOrderHistoryDateRange = (values: IOrderHistoryDateRange) => {
    this.context.orderHistoryDateRange = values;
  };

  setOrderDetailLoading = (value: IOrderListingContext['isOrderDetailLoading']) => {
    this.context.isOrderDetailLoading = value;
  };

  setOrderDetail = (value: IOrderListingContext['orderDetail']) => {
    this.context.orderDetail = value;
  };

  setFilterValues = (values: IOrderListingContext['filterFormValues']) => {
    this.context.filterFormValues = values;
  };

  setFilteredOpenPositions = (values: IOrder[]) => {
    this.context.filteredOpenPositions = values;
  };

  setFilteredPendingOrders = (values: IPendingOrder[]) => {
    this.context.filteredPendingOrders = values;
  };
}

export const orderListingService = new OrderListingService();
